import styled from '@emotion/styled';
import { Fade, Stack } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/dialogs/Dialog';
import { MenuSelect } from '../../components/MenuSelect';
import { SectionLoader } from '../../components/SectionLoader';
import StandardLibraryCheck from '../../components/StandardLibraryCheck';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TextField } from '../../components/TextField';
import { DOMAIN_BLANK } from '../../constants';
import { useAppQuery } from '../../hooks/useAppQuery';
import { IDomainListResponse, IJobListResponse } from '../../interfaces/Job';
import { useLocale } from '../../providers/LocaleProvider';
import SearchIcon from '../../resources/images/icons-search.svg';
import { KLAYO_COLORS } from '../../themes';
import SettingStandardsLibraryJobDetail from './SettingStandardsLibraryJobDetail';

export const ImportJobFromIndustryStandardDialog = ({
  theme,
  onClose,
  organization,
  open,
}: any) => {
  const isTrial = !!organization?.isTrial || !organization?.enabledFeatures?.KlayoIntelligence;
  const { translate } = useLocale();

  const [pagingModel, setPagingModel] = useState<GridPaginationModel>({ page: 0, pageSize: 5 });
  const [queryText, setQueryText] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const {
    data: similarJob,
    isFetching,
    refetch,
  } = useAppQuery<IJobListResponse>(`/StandardLibrary/jobs`, {
    config: {
      params: {
        pageNumber: pagingModel.page + 1,
        pageSize: pagingModel.pageSize,
        searchText: (queryText || '').trim(),
        ...(selectedDomain === DOMAIN_BLANK.name
          ? {
              noDomainOnly: true,
            }
          : {
              domainId: selectedDomain,
            }),
      },
    },
    placeholderData: undefined,
    enabled: organization?.enableStandardsLibrary,
  });

  const { data: domains } = useAppQuery<IDomainListResponse>(`/StandardLibrary/jobs/domains`, {
    enabled: organization?.enableStandardsLibrary,
  });

  const domainOptions = useMemo(() => {
    if (!domains) return [];

    return [
      {
        label: translate('placeholders.blank'),
        value: DOMAIN_BLANK.name,
      },
    ].concat(
      (domains?.domains || []).map((domain) => ({
        label: domain.name,
        value: domain.id,
      }))
    );
  }, [domains, translate]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagingModel(newModel);
    setSelectedRowId(null);
  };

  const onOpenDetail = () => {
    setIsDetailOpen(true);
  };

  const debouncedSetQueryText = useCallback(
    debounce((value: string) => {
      setQueryText(value);
      setPagingModel({ page: 0, pageSize: pagingModel.pageSize });
      setSelectedRowId(null);
    }, 300),
    [pagingModel, queryText]
  );

  const onChangeDomain = (e: any) => {
    setSelectedDomain(e.target.value);
    setPagingModel({ page: 0, pageSize: pagingModel.pageSize });
    setSelectedRowId(null);
    refetch();
  };

  const handleSearchChange = (e: any) => {
    setInputValue(e.target.value);
    debouncedSetQueryText(e.target.value);
  };

  const similarJobs = useMemo(() => {
    if (!similarJob) return [];
    return similarJob?.jobDefinitions.data || [];
  }, [similarJob]);

  const onCloseAllModal = () => {
    setIsDetailOpen(false);
    onClose();
  };

  return (
    <StyledImportJobFromIndustryStandardDialog
      className='klayo__import-job-from-industry-standard-dialog'
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <h1 className='truncate'>{translate('standardsLibrary.klayoIntelligenceJobs')}</h1>
      <StandardLibraryCheck>
        <>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='start'
            spacing={2}
            paddingBottom={2}
          >
            <div style={{ flex: 2 }}>
              <TextField
                dense={true}
                placeholder={translate('placeholders.searchJob')}
                autoComplete={false}
                leadingIcon={<SearchIcon />}
                fullWidth={true}
                onChange={handleSearchChange}
                value={inputValue}
              />
            </div>
            <div style={{ flex: 1 }}>
              <MenuSelect
                className='klayo_practical-type-select-menu'
                value={selectedDomain}
                firstValueDefault={false}
                noSelectionError={false}
                dense={true}
                placeholder={translate('labels.domain')}
                disabled={false}
                items={domainOptions}
                onChange={onChangeDomain}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root fieldset': {
                    top: '0px !important',
                  },
                }}
              />
            </div>
          </Stack>

          {isFetching ? (
            <SectionLoader />
          ) : (
            <StandardLibraryTable
              totalCount={similarJob?.jobDefinitions?.totalCount}
              rowHasAction={true}
              dense={true}
              rowHeight={44}
              paper={false}
              theme={theme}
              queryText={queryText}
              showHeader={false}
              isLoading={isFetching}
              columns={[
                { type: StandardLibraryTable.columns.nameSuggestion },
                { type: StandardLibraryTable.columns.domain },
              ]}
              paginationModel={pagingModel}
              onPaginationModelChange={handlePaginationModelChange}
              paginationMode='server'
              rows={(similarJobs ? similarJobs : null) as any}
              rowSelectionModel={selectedRowId ? [selectedRowId] : []}
              onJobSelect={(_event: any, row: any) => {
                setSelectedRowId((prev) => (prev === row.id ? null : row.id));
              }}
              showFooter={false}
              hideFirstLastBorder
            />
          )}
        </>
      </StandardLibraryCheck>

      <div className='button-container'>
        <div className='button-container-left'>
          <Button
            size='md'
            theme={theme}
            label={
              organization?.enableStandardsLibrary
                ? translate('common.cancel')
                : translate('common.close')
            }
            onClick={onClose}
          />
        </div>
        {organization?.enableStandardsLibrary && (
          <div className='klaro_regulations-button-container'>
            <Button
              size='md'
              theme={theme}
              variant='filled'
              label={
                <p style={{ fontSize: 14, padding: '0 .3rem' }}>
                  {translate('placeholders.reviewAndImport')}
                </p>
              }
              disabled={!selectedRowId}
              onClick={onOpenDetail}
            />
          </div>
        )}
      </div>

      <Fade in={isDetailOpen} timeout={300} unmountOnExit>
        <div>
          <SettingStandardsLibraryJobDetail
            theme={theme}
            open={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            jobId={selectedRowId}
            onCloseAllDialog={onCloseAllModal}
            isTrial={isTrial}
          />
        </div>
      </Fade>
    </StyledImportJobFromIndustryStandardDialog>
  );
};

const StyledImportJobFromIndustryStandardDialog = styled(Dialog)`
  &.klayo__import-job-from-industry-standard-dialog {
    .klaro_regulations-button-container:has(button[aria-disabled='true']) {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: ${KLAYO_COLORS.GreyGR200} !important;
    }

    h1 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px; /* 133.333% */
      letter-spacing: -0.5px;
      margin-top: 0px;
      margin-bottom: 32px;
    }
    .button-container {
      display: flex;
      justify-content: flex-end;
      padding-bottom: 30px;
      padding-top: 30px;
      gap: 10px;
    }
    h2 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-top: 0px;
      margin-bottom: 24px;
    }

    .main-title {
      font-weight: 700;
    }

    .MuiDialogContent-root {
      padding: 40px 40px 0px 40px;
    }

    .MuiPaper-root {
      max-width: 800px;
    }

    .MuiDataGrid-cell {
      border-bottom: none !important;
    }

    .MuiDataGrid-footerContainer {
      border-top: none !important;
    }

    .MuiDataGrid-row {
      border-radius: 7px !important;

      &[aria-selected='true'] {
        background-color: ${KLAYO_COLORS.NeonBlueNB500} !important;

        & > *,
        & .MuiTypography-root {
          color: ${KLAYO_COLORS.WhiteWH900} !important;
        }
      }
    }

    .MuiDataGrid-selectedRowCount {
      visibility: hidden !important;
    }
  }
`;
